import {
  ScoutPrivateDocumentExtractionStatus,
  ScoutPrivateDocumentIngestionMode,
} from "generated-graphql/graphql";

import type { ScoutConversationPrivateDocumentRow } from "../hooks/useScoutConversationPrivateDocuments";

export type ScoutExtractionPipelineStageId = "preview" | "extracting";

export type ScoutExtractionPipelineStageState = "pending" | "active" | "complete" | "failed";

export type ScoutExtractionPipelineStage = {
  id: ScoutExtractionPipelineStageId;
  label: string;
  state: ScoutExtractionPipelineStageState;
};

export function formatScoutDocumentSizeBytes(sizeBytes: number | undefined): string | undefined {
  if (sizeBytes === undefined || sizeBytes <= 0) {
    return undefined;
  }

  if (sizeBytes >= 1024 * 1024) {
    return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (sizeBytes >= 1024) {
    return `${(sizeBytes / 1024).toFixed(0)} KB`;
  }

  return `${sizeBytes} B`;
}

export function formatScoutDocumentPageCount(input: {
  pageCount?: number | null;
  extractionProgress?: ScoutConversationPrivateDocumentRow["extractionProgress"];
}): string | undefined {
  if (input.pageCount && input.pageCount > 0) {
    return `${input.pageCount} pages`;
  }

  const pagesTotal = input.extractionProgress?.pagesTotal;
  if (pagesTotal && pagesTotal > 0) {
    return `${pagesTotal} pages`;
  }

  return undefined;
}

/** Compact relative time for the extraction card subheader (e.g. "about 2 min ago"). */
export function formatScoutExtractionStartedAgo(
  startedAtMs: number | undefined,
  now: number
): string {
  if (startedAtMs === undefined) {
    return "just now";
  }

  const deltaMs = Math.max(0, now - startedAtMs);
  const seconds = Math.floor(deltaMs / 1000);
  if (seconds < 45) {
    return "just now";
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `about ${minutes} min ago`;
  }

  const hours = Math.floor(minutes / 60);
  return `about ${hours} hr ago`;
}

export function formatScoutExtractionTimeRemaining(input: {
  pagesCompleted: number;
  pagesTotal?: number | null;
  startedAtMs: number | undefined;
  now: number;
}): string | undefined {
  const pagesTotal = input.pagesTotal;
  if (
    !pagesTotal ||
    pagesTotal <= 0 ||
    input.pagesCompleted <= 0 ||
    input.startedAtMs === undefined
  ) {
    return undefined;
  }

  const remainingPages = Math.max(0, pagesTotal - input.pagesCompleted);
  if (remainingPages === 0) {
    return undefined;
  }

  const elapsedMs = Math.max(1, input.now - input.startedAtMs);
  const msPerPage = elapsedMs / input.pagesCompleted;
  const remainingMinutes = Math.max(1, Math.ceil((msPerPage * remainingPages) / 60_000));

  if (remainingMinutes === 1) {
    return "about 1 min left";
  }

  return `about ${remainingMinutes} min left`;
}

export function getScoutExtractionPipelineStages(
  document: ScoutConversationPrivateDocumentRow
): ScoutExtractionPipelineStage[] {
  const previewState = ((): ScoutExtractionPipelineStageState => {
    if (document.extractionStatus === ScoutPrivateDocumentExtractionStatus.PreviewRunning) {
      return "active";
    }
    if (
      document.extractionStatus === ScoutPrivateDocumentExtractionStatus.Pending ||
      document.extractionStatus === ScoutPrivateDocumentExtractionStatus.Running ||
      document.extractionStatus === ScoutPrivateDocumentExtractionStatus.Succeeded ||
      document.extractionStatus === ScoutPrivateDocumentExtractionStatus.Failed
    ) {
      return "complete";
    }
    return "pending";
  })();

  const extractionState = ((): ScoutExtractionPipelineStageState => {
    if (document.extractionStatus === ScoutPrivateDocumentExtractionStatus.PreviewRunning) {
      return "pending";
    }
    if (
      document.extractionStatus === ScoutPrivateDocumentExtractionStatus.Pending ||
      document.extractionStatus === ScoutPrivateDocumentExtractionStatus.Running
    ) {
      return "active";
    }
    if (document.extractionStatus === ScoutPrivateDocumentExtractionStatus.Succeeded) {
      return "complete";
    }
    if (document.extractionStatus === ScoutPrivateDocumentExtractionStatus.Failed) {
      return "failed";
    }
    return "pending";
  })();

  const previewLabel = previewState === "active" ? "Reading…" : "Reading";
  const extractingLabel = extractionState === "active" ? "Extracting…" : "Extracting";

  return [
    { id: "preview", label: previewLabel, state: previewState },
    { id: "extracting", label: extractingLabel, state: extractionState },
  ];
}

export type ScoutLargeDocumentExtractionProgressUi = {
  headlineStatus: string;
  startedAgo?: string;
  pageProgressLabel?: string;
  timeRemainingLabel?: string;
  progressVariant: "determinate" | "indeterminate";
  progressValue?: number;
  showProgressBar: boolean;
  pipelineStages: ScoutExtractionPipelineStage[];
};

export function getScoutLargeDocumentExtractionProgressUi(
  document: ScoutConversationPrivateDocumentRow,
  options: { startedAtMs?: number; now: number }
): ScoutLargeDocumentExtractionProgressUi {
  const progress = document.extractionProgress;
  const pagesTotal = progress?.pagesTotal;
  const pagesCompleted = progress?.pagesCompleted ?? 0;
  const startedAgo = formatScoutExtractionStartedAgo(options.startedAtMs, options.now);

  if (document.extractionStatus === ScoutPrivateDocumentExtractionStatus.PreviewRunning) {
    return {
      headlineStatus: "Scanning…",
      startedAgo,
      showProgressBar: true,
      progressVariant: "indeterminate",
      pipelineStages: getScoutExtractionPipelineStages(document),
    };
  }

  if (document.extractionStatus === ScoutPrivateDocumentExtractionStatus.Pending) {
    return {
      headlineStatus: "Analyzing…",
      startedAgo,
      showProgressBar: true,
      progressVariant: "indeterminate",
      pipelineStages: getScoutExtractionPipelineStages(document),
    };
  }

  if (
    document.extractionStatus === ScoutPrivateDocumentExtractionStatus.Running &&
    pagesTotal &&
    pagesTotal > 0
  ) {
    const clampedCompleted = Math.min(pagesCompleted, pagesTotal);
    return {
      headlineStatus: "Analyzing…",
      startedAgo,
      pageProgressLabel: `Page ${clampedCompleted} of ${pagesTotal}`,
      timeRemainingLabel: formatScoutExtractionTimeRemaining({
        pagesCompleted: clampedCompleted,
        pagesTotal,
        startedAtMs: options.startedAtMs,
        now: options.now,
      }),
      showProgressBar: true,
      progressVariant: "determinate",
      progressValue: (clampedCompleted / pagesTotal) * 100,
      pipelineStages: getScoutExtractionPipelineStages(document),
    };
  }

  if (document.extractionStatus === ScoutPrivateDocumentExtractionStatus.Running) {
    return {
      headlineStatus: "Analyzing…",
      startedAgo,
      showProgressBar: true,
      progressVariant: "determinate",
      progressValue: 0,
      pipelineStages: getScoutExtractionPipelineStages(document),
    };
  }

  if (document.extractionStatus === ScoutPrivateDocumentExtractionStatus.Succeeded) {
    return {
      headlineStatus: "Analysis complete",
      showProgressBar: false,
      progressVariant: "determinate",
      progressValue: 100,
      pageProgressLabel: `Page ${pagesCompleted} of ${pagesTotal}`,
      timeRemainingLabel: "Document ready",
      pipelineStages: getScoutExtractionPipelineStages(document),
    };
  }

  if (document.extractionStatus === ScoutPrivateDocumentExtractionStatus.Failed) {
    return {
      headlineStatus: "Analysis failed",
      showProgressBar: false,
      progressVariant: "determinate",
      progressValue: 0,
      pipelineStages: getScoutExtractionPipelineStages(document),
    };
  }

  return {
    headlineStatus: "Analyzing…",
    startedAgo,
    showProgressBar: false,
    progressVariant: "indeterminate",
    pipelineStages: getScoutExtractionPipelineStages(document),
  };
}

export type ScoutLargeDocumentOptInUiState = {
  statusTitle: string;
  statusDetail?: string;
  metaLine?: string;
};

export function getScoutLargeDocumentOptInUiState(
  document: ScoutConversationPrivateDocumentRow,
  options?: { previewOnlyDeclined?: boolean }
): ScoutLargeDocumentOptInUiState {
  const sizeLabel = formatScoutDocumentSizeBytes(document.sizeBytes);
  const pageLabel = formatScoutDocumentPageCount({
    pageCount: document.pageCount,
    extractionProgress: document.extractionProgress,
  });
  const metaParts = [pageLabel, sizeLabel].filter((part): part is string => Boolean(part));
  const metaLine = metaParts.length > 0 ? metaParts.join(" · ") : undefined;

  if (
    document.extractionStatus === ScoutPrivateDocumentExtractionStatus.FullAnalysisDeclined ||
    options?.previewOnlyDeclined
  ) {
    return {
      statusTitle: "Using preview only",
      statusDetail:
        document.previewSummary ??
        "Scout is answering from the first pages only. Upload again to run full analysis.",
      metaLine,
    };
  }

  return {
    statusTitle: "Preview ready",
    statusDetail:
      document.previewSummary ??
      "Confirm below to analyze the full document, or ask questions using the preview only.",
    metaLine,
  };
}

/** Statuses that show the in-thread extraction progress card. */
export const SCOUT_LARGE_DOCUMENT_EXTRACTION_PROGRESS_STATUSES = new Set([
  ScoutPrivateDocumentExtractionStatus.PreviewRunning,
  ScoutPrivateDocumentExtractionStatus.Pending,
  ScoutPrivateDocumentExtractionStatus.Running,
  ScoutPrivateDocumentExtractionStatus.Succeeded,
  ScoutPrivateDocumentExtractionStatus.Failed,
]);

export function isScoutLargeDocumentExtractionInProgress(
  document: ScoutConversationPrivateDocumentRow | undefined
): document is ScoutConversationPrivateDocumentRow {
  return (
    document !== undefined &&
    document.ingestionMode === ScoutPrivateDocumentIngestionMode.LargePdfExtraction &&
    SCOUT_LARGE_DOCUMENT_EXTRACTION_PROGRESS_STATUSES.has(document.extractionStatus)
  );
}
